import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  flexRender,
} from '@tanstack/react-table';
import { useTablePreferences } from './useTablePreferences';
import ColumnVisibilityMenu from './ColumnVisibilityMenu';
import TableFilters from './TableFilters';
import { parseDateSafely } from './tableUtils';
import { normalizeStatus } from '../../constants';
import './DataTable.css';

/**
 * Indeterminate Checkbox Component
 * Used for select-all checkbox in table header
 */
function IndeterminateCheckbox({ indeterminate, checked, onChange, ...rest }) {
  const ref = useRef(null);

  useEffect(() => {
    if (ref.current) {
      ref.current.indeterminate = indeterminate;
    }
  }, [indeterminate]);

  return (
    <input
      type="checkbox"
      ref={ref}
      checked={checked}
      onChange={onChange}
      className="form-check-input"
      {...rest}
    />
  );
}

/**
 * DataTable - Reusable table component with TanStack Table
 * 
 * Features:
 * - Column resizing (drag borders)
 * - Column visibility (show/hide)
 * - Column reordering (drag & drop)
 * - Row selection (checkboxes)
 * - Sorting
 * - Filtering
 * - User preferences (saved to localStorage)
 * - Responsive (mobile card view, desktop table)
 * - Dark mode support
 * 
 * @param {Array} columns - Column definitions (see constants/tableColumns.js)
 * @param {Array} data - Table data
 * @param {string} tableId - Unique ID for saving preferences
 * @param {boolean} enableRowSelection - Show row checkboxes
 * @param {boolean} enableColumnResizing - Allow column resize
 * @param {boolean} enableColumnOrdering - Allow column reorder
 * @param {boolean} enableFilters - Show filter bar
 * @param {object} filterConfig - Custom filter configuration
 * @param {function} onRowClick - Callback when row is clicked
 * @param {function} renderRowActions - Function to render action buttons per row
 * @param {boolean} enablePagination - Enable pagination
 * @param {number} pageSize - Initial page size
 */

export default function DataTable({
  columns,
  data,
  tableId = 'default-table',
  enableRowSelection = false,
  enableColumnResizing = true,
  enableColumnOrdering = true,
  enableFilters = true,
  filterConfig = null,
  onRowClick = null,
  renderRowActions = null,
  enablePagination = false,
  pageSize = 50,
  className = '',
  meta = {}, // Meta data for column cells (e.g. updateWorkType, updateUserStatus)
  defaultSorting = [], // Default sorting state e.g. [{ id: 'name', desc: false }]
  rowSelection: externalRowSelection = null, // External row selection state (optional)
  onRowSelectionChange: externalOnRowSelectionChange = null, // External row selection handler
}) {
  // Load saved preferences
  const { preferences, savePreferences, resetPreferences } = useTablePreferences(tableId, {
    columnVisibility: {},
    columnOrder: columns.map(c => c.accessorKey || c.id),
    columnSizing: {},
  });

  // Table state
  const [sorting, setSorting] = useState(defaultSorting);
  const [columnVisibility, setColumnVisibility] = useState(preferences.columnVisibility || {});
  
  // Ensure 'select' column is always first if row selection is enabled
  const initialColumnOrder = useMemo(() => {
    const baseOrder = preferences.columnOrder || columns.map(c => c.accessorKey || c.id);
    if (enableRowSelection) {
      // Remove 'select' from wherever it is, then add it to the front
      const withoutSelect = baseOrder.filter(id => id !== 'select');
      return ['select', ...withoutSelect];
    }
    return baseOrder;
  }, [preferences.columnOrder, columns, enableRowSelection]);
  
  const [columnOrder, setColumnOrder] = useState(initialColumnOrder);
  const [columnSizing, setColumnSizing] = useState(preferences.columnSizing || {});
  // Use external row selection if provided, otherwise internal state
  const [internalRowSelection, setInternalRowSelection] = useState({});
  const rowSelection = externalRowSelection !== null ? externalRowSelection : internalRowSelection;
  const setRowSelection = externalOnRowSelectionChange !== null ? externalOnRowSelectionChange : setInternalRowSelection;
  const [globalFilter, setGlobalFilter] = useState('');
  
  // Load custom filters from localStorage on mount
  const [customFilters, setCustomFilters] = useState(() => {
    try {
      const saved = localStorage.getItem(`${tableId}-filters`);
      return saved ? JSON.parse(saved) : {};
    } catch (error) {
      console.error('Error loading filters:', error);
      return {};
    }
  });

  // Save preferences when they change (with debounce to prevent loops)
  useEffect(() => {
    const timer = setTimeout(() => {
      savePreferences({
        columnVisibility,
        columnOrder,
        columnSizing,
      });
    }, 500); // Debounce 500ms
    
    return () => clearTimeout(timer);
  }, [columnVisibility, columnOrder, columnSizing]);

  // Save filters to localStorage when they change
  useEffect(() => {
    try {
      localStorage.setItem(`${tableId}-filters`, JSON.stringify(customFilters));
    } catch (error) {
      console.error('Error saving filters:', error);
    }
  }, [customFilters, tableId]);

  // Add selection column if enabled
  const columnsWithSelection = useMemo(() => {
    if (!enableRowSelection) return columns;
    
    return [
      {
        id: 'select',
        header: ({ table }) => (
          <IndeterminateCheckbox
            {...{
              checked: table.getIsAllRowsSelected(),
              indeterminate: table.getIsSomeRowsSelected(),
              onChange: table.getToggleAllRowsSelectedHandler(),
            }}
          />
        ),
        cell: ({ row }) => (
          <input
            type="checkbox"
            checked={row.getIsSelected()}
            disabled={!row.getCanSelect()}
            onChange={row.getToggleSelectedHandler()}
            onClick={(e) => e.stopPropagation()} // Prevent row click when clicking checkbox
            className="form-check-input"
          />
        ),
        enableSorting: false,
        enableHiding: false,
        size: 50,
      },
      ...columns,
    ];
  }, [columns, enableRowSelection]);

  // Add actions column if provided
  const columnsWithActions = useMemo(() => {
    if (!renderRowActions) return columnsWithSelection;
    
    return [
      ...columnsWithSelection,
      {
        id: 'actions',
        header: 'Actions',
        cell: ({ row }) => renderRowActions(row.original),
        enableSorting: false,
        enableHiding: false,
      },
    ];
  }, [columnsWithSelection, renderRowActions]);

  // Custom sorting based on sortBy filter
  const sortedAndFilteredData = useMemo(() => {
    if (!customFilters || Object.keys(customFilters).length === 0) return data;
    
    // First, filter the data
    let result = data.filter(row => {
      // Apply each custom filter
      for (const [key, value] of Object.entries(customFilters)) {
        // Skip sortBy - it's not a filter
        if (key === 'sortBy') continue;
        
        // Handle hideIgnored checkbox specially (now means "hide Irrelevant", including
        // legacy ignore/ignored rows - see constants.js normalizeStatus)
        if (key === 'hideIgnored') {
          if (value === true && normalizeStatus(row.user_status) === 'irrelevant') {
            return false;
          }
          continue;
        }

        // Skip empty/all filters
        if (value === 'all' || value === '' || value == null) continue;

        // Handle search term
        if (key === 'searchTerm') {
          const searchLower = String(value).toLowerCase();
          const matchesSearch = Object.values(row).some(val =>
            val != null && String(val).toLowerCase().includes(searchLower)
          );
          if (!matchesSearch) return false;
        } else if (key === 'work_type') {
          // Case-insensitive match for work_type (data might have mixed cases)
          const rowValue = String(row[key] || '').toLowerCase();
          const filterValue = String(value).toLowerCase();
          if (rowValue !== filterValue) return false;
        } else if (key === 'user_status') {
          // Normalize legacy user_status values so old rows still match their new filter bucket
          if (normalizeStatus(row.user_status) !== value) return false;
        } else {
          // Standard equality filter
          if (row[key] !== value) return false;
        }
      }
      return true;
    });

    // Then, apply custom sorting if sortBy is specified
    const sortBy = customFilters.sortBy;
    if (sortBy && sortBy !== 'all') {
      result = [...result].sort((a, b) => {
        switch (sortBy) {
          case 'due_date_asc': {
            // Soonest first (default)
            const dateA = parseDateSafely(a.due_date);
            const dateB = parseDateSafely(b.due_date);
            const now = new Date();
            
            if (!dateA && !dateB) return 0;
            if (!dateA) return 1;
            if (!dateB) return -1;
            
            const isPastA = dateA < now;
            const isPastB = dateB < now;
            
            if (isPastA && !isPastB) return 1;
            if (!isPastA && isPastB) return -1;
            
            return dateA - dateB;
          }
          
          case 'due_date_desc': {
            // Most time remaining first
            const dateA = parseDateSafely(a.due_date);
            const dateB = parseDateSafely(b.due_date);
            const now = new Date();
            
            if (!dateA && !dateB) return 0;
            if (!dateA) return 1;
            if (!dateB) return -1;
            
            const isPastA = dateA < now;
            const isPastB = dateB < now;
            
            // Past dates go to end
            if (isPastA && !isPastB) return 1;
            if (!isPastA && isPastB) return -1;
            
            // Both future: furthest first
            return dateB - dateA;
          }
          
          case 'date_added': {
            // Newest first
            const addedA = a.first_seen ? new Date(a.first_seen) : new Date(0);
            const addedB = b.first_seen ? new Date(b.first_seen) : new Date(0);
            return addedB - addedA;
          }
          
          case 'organization':
            return (a.organization || '').localeCompare(b.organization || '');
          
          case 'work_type':
            return (a.work_type || '').localeCompare(b.work_type || '');
          
          default:
            return 0;
        }
      });
    }
    
    return result;
  }, [data, customFilters]);

  // Legacy filteredData for backward compatibility
  const filteredData = useMemo(() => {
    if (!customFilters || Object.keys(customFilters).length === 0) return data;
    
    return data.filter(row => {
      // Apply each custom filter
      for (const [key, value] of Object.entries(customFilters)) {
        // Handle hideIgnored checkbox specially (now means "hide Irrelevant", including
        // legacy ignore/ignored rows - see constants.js normalizeStatus)
        if (key === 'hideIgnored') {
          // If hideIgnored is checked (true), filter out irrelevant jobs
          if (value === true && normalizeStatus(row.user_status) === 'irrelevant') {
            return false;
          }
          // If hideIgnored is unchecked (false), don't filter anything
          continue;
        }

        // Skip empty/all filters
        if (value === 'all' || value === '' || value == null) continue;

        // Handle search term
        if (key === 'searchTerm') {
          const searchLower = String(value).toLowerCase();
          const matchesSearch = Object.values(row).some(val =>
            val != null && String(val).toLowerCase().includes(searchLower)
          );
          if (!matchesSearch) return false;
        } else if (key === 'user_status') {
          // Normalize legacy user_status values so old rows still match their new filter bucket
          if (normalizeStatus(row.user_status) !== value) return false;
        } else {
          // Standard equality filter (for work_type, organization, source)
          if (row[key] !== value) return false;
        }
      }
      return true;
    });
  }, [data, customFilters]);
  
  // Use sortedAndFilteredData if sortBy is present, otherwise use filteredData
  const finalData = customFilters?.sortBy ? sortedAndFilteredData : filteredData;

  // Track if currently resizing
  const [isResizing, setIsResizing] = useState(false);

  // Auto-fit column width based on content (Excel-like double-click)
  const autoFitColumn = (column, tableInstance) => {
    // Create a temporary canvas to measure text width
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    context.font = '14px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
    
    let maxWidth = 0;
    
    // Measure header text (including sort indicator space)
    const headerText = typeof column.columnDef.header === 'string' 
      ? column.columnDef.header 
      : column.id;
    const headerWidth = context.measureText(headerText).width;
    maxWidth = Math.max(maxWidth, headerWidth);
    
    // Measure a sample of cell contents (first 50 visible rows for performance)
    const rows = tableInstance.getRowModel().rows.slice(0, 50);
    rows.forEach(row => {
      const cell = row.getVisibleCells().find(c => c.column.id === column.id);
      if (cell) {
        let cellText = '';
        const value = cell.getValue();
        
        // Try to extract text content from the cell
        if (typeof value === 'string' || typeof value === 'number') {
          cellText = String(value);
        } else if (value?.toString && typeof value.toString === 'function') {
          cellText = value.toString();
        }
        
        if (cellText) {
          // For badges/complex content, add extra width multiplier
          const widthMultiplier = cellText.includes('job') || cellText.includes('%') ? 1.5 : 1;
          const cellWidth = context.measureText(cellText).width * widthMultiplier;
          maxWidth = Math.max(maxWidth, cellWidth);
        }
      }
    });
    
    // Add padding: 50px for padding, sorting icon, badges, etc.
    const optimalWidth = Math.ceil(maxWidth) + 50;
    
    // Respect column's minSize if defined, otherwise use reasonable defaults
    const columnMinSize = column.columnDef.minSize || 60;
    const columnMaxSize = column.columnDef.maxSize || 600;
    
    // Set bounds based on column definition
    const finalWidth = Math.max(columnMinSize, Math.min(optimalWidth, columnMaxSize));
    
    // Update the column sizing state
    setColumnSizing(prev => ({
      ...prev,
      [column.id]: finalWidth
    }));
  };

  // Initialize table
  const table = useReactTable({
    data: finalData,
    columns: columnsWithActions,
    state: {
      sorting,
      columnVisibility,
      columnOrder,
      columnSizing,
      rowSelection,
      globalFilter,
    },
    meta: meta, // Pass meta to column cells (updateWorkType, updateUserStatus, etc.)
    enableRowSelection: enableRowSelection,
    enableColumnResizing: enableColumnResizing,
    columnResizeMode: 'onChange',
    columnResizeDirection: 'ltr',
    enableSorting: true,
    onSortingChange: setSorting,
    onColumnVisibilityChange: setColumnVisibility,
    onColumnOrderChange: setColumnOrder,
    onColumnSizingChange: setColumnSizing,
    onRowSelectionChange: setRowSelection,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: enablePagination ? getPaginationRowModel() : undefined,
    initialState: {
      pagination: enablePagination ? {
        pageIndex: 0,
        pageSize: pageSize,
      } : undefined,
    },
    // Fix resize sensitivity
    defaultColumn: {
      minSize: 50,
      maxSize: 800,
      size: 150,
    },
  });

  // Detect when resizing starts/stops
  useEffect(() => {
    const anyColumnResizing = table.getAllColumns().some(col => col.getIsResizing());
    setIsResizing(anyColumnResizing);
  }, [table, columnSizing]);

  // Add escape handler to cancel stuck resize operations
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isResizing) {
        setIsResizing(false);
        // Force reset all column resize states
        table.getAllColumns().forEach(col => {
          if (col.getIsResizing()) {
            col.resetSize();
          }
        });
      }
    };
    
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isResizing, table]);

  // Get selected rows
  const selectedRows = useMemo(() => {
    return table.getSelectedRowModel().rows.map(row => row.original);
  }, [table, rowSelection]);

  return (
    <div className={`data-table-container ${className}`}>
      {/* Toolbar */}
      <div className="data-table-toolbar">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div className="d-flex align-items-center gap-3">
            <span className="text-muted">
              Showing {table.getFilteredRowModel().rows.length} of {data.length} rows
            </span>
            {selectedRows.length > 0 && (
              <span className="text-primary fw-bold">
                {selectedRows.length} selected
              </span>
            )}
            {/* Column Visibility Menu - Moved to left side */}
            <ColumnVisibilityMenu
              table={table}
              onReset={() => {
                resetPreferences();
                setColumnVisibility({});
                setColumnOrder(columns.map(c => c.accessorKey || c.id));
                setColumnSizing({});
              }}
            />
          </div>
        </div>
        
        {/* Custom Filters */}
        {enableFilters && filterConfig && (
          <TableFilters
            filterConfig={filterConfig}
            filters={customFilters}
            onFilterChange={setCustomFilters}
            onClearFilters={() => {
              setCustomFilters({});
              setGlobalFilter('');
            }}
          />
        )}
      </div>

      {/* Desktop Table View */}
      <div className="table-responsive d-none d-md-block" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
        <table 
          className={`table table-hover data-table ${isResizing ? 'is-resizing' : ''}`}
          style={{ width: table.getCenterTotalSize() }}
        >
          <thead>
            {table.getHeaderGroups().map(headerGroup => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map(header => (
                  <th
                    key={header.id}
                    draggable={!header.column.getIsGrouped()} // Enable drag for reordering
                    onDragStart={(e) => {
                      // Prevent drag if clicking near resize handle (right 20px)
                      const rect = e.currentTarget.getBoundingClientRect();
                      const clickX = e.clientX - rect.left;
                      const isNearRightEdge = clickX > rect.width - 20;
                      
                      if (isNearRightEdge || e.target.classList.contains('resizer')) {
                        e.preventDefault();
                        return false;
                      }
                      
                      e.dataTransfer.effectAllowed = 'move';
                      e.dataTransfer.setData('text/plain', header.column.id);
                    }}
                    onDragOver={(e) => {
                      e.preventDefault();
                      e.dataTransfer.dropEffect = 'move';
                      e.currentTarget.classList.add('drag-over');
                    }}
                    onDragLeave={(e) => {
                      e.currentTarget.classList.remove('drag-over');
                    }}
                    onDrop={(e) => {
                      e.preventDefault();
                      e.currentTarget.classList.remove('drag-over');
                      const draggedColumnId = e.dataTransfer.getData('text/plain');
                      const targetColumnId = header.column.id;
                      
                      if (draggedColumnId !== targetColumnId) {
                        const currentOrder = table.getState().columnOrder;
                        const dragIndex = currentOrder.indexOf(draggedColumnId);
                        const targetIndex = currentOrder.indexOf(targetColumnId);
                        
                        const newOrder = [...currentOrder];
                        newOrder.splice(dragIndex, 1);
                        newOrder.splice(targetIndex, 0, draggedColumnId);
                        
                        setColumnOrder(newOrder);
                      }
                    }}
                    style={{ 
                      width: header.getSize(),
                      position: 'relative',
                      cursor: header.column.getCanSort() ? 'pointer' : 'grab',
                    }}
                    onClick={(e) => {
                      // Don't sort if clicking on resize handle
                      if (e.target.classList.contains('resizer')) {
                        return;
                      }
                      header.column.getToggleSortingHandler()(e);
                    }}
                    title={header.column.getCanSort() ? 'Click to sort, drag to reorder' : 'Drag to reorder'}
                  >
                    <div className="d-flex align-items-center justify-content-between">
                      <span>
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                      </span>
                      {header.column.getCanSort() && (
                        <span className="ms-1">
                          {{
                            asc: ' 🔼',
                            desc: ' 🔽',
                          }[header.column.getIsSorted()] ?? ' ⏺️'}
                        </span>
                      )}
                    </div>
                    
                    {/* Resize handle */}
                    {enableColumnResizing && header.column.getCanResize() && (
                      <div
                        draggable={false}
                        onDragStart={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          return false;
                        }}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          e.stopPropagation(); // Prevent sort and drag from triggering
                          header.getResizeHandler()(e);
                        }}
                        onTouchStart={(e) => {
                          e.preventDefault();
                          e.stopPropagation(); // Prevent sort and drag from triggering
                          header.getResizeHandler()(e);
                        }}
                        onClick={(e) => {
                          e.stopPropagation(); // Prevent sort from triggering
                        }}
                        onDoubleClick={(e) => {
                          e.stopPropagation(); // Prevent sort from triggering
                          autoFitColumn(header.column, table);
                        }}
                        className={`resizer ${header.column.getIsResizing() ? 'isResizing' : ''}`}
                        title="Drag to resize. Double-click to auto-fit."
                      />
                    )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.length === 0 ? (
              <tr>
                <td colSpan={table.getAllColumns().length} className="text-center text-muted py-4">
                  No data to display
                </td>
              </tr>
            ) : (
              table.getRowModel().rows.map(row => {
                // Support custom row styling (e.g., red rows for errors)
                let rowClassName = row.getIsSelected() ? 'table-active' : '';
                
                // Check for error/danger rows (used in AI Monitoring email logs)
                if (row.original.success === false) {
                  rowClassName = 'table-danger';
                }
                
                return (
                  <tr
                    key={row.id}
                    onClick={() => onRowClick && onRowClick(row.original)}
                    style={{ cursor: onRowClick ? 'pointer' : 'default' }}
                    className={rowClassName}
                  >
                  {row.getVisibleCells().map(cell => (
                    <td key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Mobile Card View */}
      <div className="d-md-none mobile-card-view">
        {table.getRowModel().rows.length === 0 ? (
          <div className="text-center text-muted py-4">
            No data to display
          </div>
        ) : (
          table.getRowModel().rows.map(row => (
            <div
              key={row.id}
              className="mobile-card"
              onClick={() => onRowClick && onRowClick(row.original)}
            >
              {row.getVisibleCells().map(cell => {
                if (cell.column.id === 'select' || cell.column.id === 'actions') {
                  return null; // Skip these in mobile view
                }
                return (
                  <div key={cell.id} className="mobile-card-row">
                    <div className="mobile-card-label">
                      {cell.column.columnDef.header}:
                    </div>
                    <div className="mobile-card-value">
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </div>
                  </div>
                );
              })}
              {renderRowActions && (
                <div className="mobile-card-actions mt-2">
                  {renderRowActions(row.original)}
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Pagination */}
      {enablePagination && (
        <div className="d-flex justify-content-between align-items-center mt-3">
          <div>
            <button
              className="btn btn-sm btn-outline-primary me-1"
              onClick={() => table.setPageIndex(0)}
              disabled={!table.getCanPreviousPage()}
            >
              {'<<'}
            </button>
            <button
              className="btn btn-sm btn-outline-primary me-1"
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
            >
              {'<'}
            </button>
            <button
              className="btn btn-sm btn-outline-primary me-1"
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
            >
              {'>'}
            </button>
            <button
              className="btn btn-sm btn-outline-primary"
              onClick={() => table.setPageIndex(table.getPageCount() - 1)}
              disabled={!table.getCanNextPage()}
            >
              {'>>'}
            </button>
          </div>
          <span className="text-muted">
            Page {table.getState().pagination.pageIndex + 1} of {table.getPageCount()}
          </span>
          <select
            className="form-select form-select-sm"
            style={{ width: 'auto' }}
            value={table.getState().pagination.pageSize}
            onChange={e => table.setPageSize(Number(e.target.value))}
          >
            {[10, 25, 50, 100].map(size => (
              <option key={size} value={size}>
                Show {size}
              </option>
            ))}
          </select>
        </div>
      )}
    </div>
  );
}

